import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "../lib/route-primitives";
import { useAdminApi } from "../hooks/use-admin-api";
import { useWedding } from "../contexts/wedding-context";
import { Loader2, Users, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";

interface OrganizerRow {
  id: string;
  email: string;
  display_name: string | null;
  phone_e164: string | null;
  plan: string | null;
  created_at: string;
}

const AVATAR_COLORS = [
  "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
  "bg-primary/10 text-primary",
  "bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300",
];

function avatarColor(email: string): string {
  const hash = [...email].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getInitials(name: string | null, email: string): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  return email[0]?.toUpperCase() ?? "?";
}

export function WeddingOrganizersPage() {
  const { t, i18n } = useTranslation();
  const adminApi = useAdminApi();
  const { slug } = useWedding();

  const navigate = useAppNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", slug, "organizers"],
    queryFn: () => adminApi.get<OrganizerRow[]>("/organizers"),
  });

  const organizers = data?.data ?? [];

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="font-serif italic text-3xl text-foreground">{t("admin.organizersTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.organizerAccessHint")}</p>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30 h-10 animate-pulse" />
          <div className="divide-y divide-border">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="h-10 w-10 rounded-full bg-muted animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-48 rounded-md bg-muted animate-pulse" />
                  <div className="h-3 w-64 rounded-md bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : organizers.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card flex flex-col items-center justify-center py-20 gap-3">
          <Users className="h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{t("admin.organizersEmpty")}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-primary/60" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t("admin.organizersTitle")}
            </p>
            <span className="ml-auto text-xs font-semibold text-muted-foreground">{organizers.length}</span>
          </div>
          <div className="divide-y divide-border">
            {organizers.map((org) => (
              <div
                key={org.id}
                onClick={() => navigate(paths.userDetail(org.id))}
                className="flex items-center gap-4 px-5 py-4 hover:bg-primary/5 transition-colors cursor-pointer"
              >
                <div className={cn(
                  "h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold",
                  avatarColor(org.email)
                )}>
                  {getInitials(org.display_name, org.email)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {org.display_name && (
                      <span className="text-sm font-semibold text-foreground truncate">{org.display_name}</span>
                    )}
                    <span className="text-sm text-muted-foreground truncate">{org.email}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {org.phone_e164 && (
                      <span className="text-xs text-muted-foreground font-mono">{org.phone_e164}</span>
                    )}
                    <span className="text-xs text-muted-foreground/60">
                      {new Date(org.created_at).toLocaleDateString(i18n.language)}
                    </span>
                  </div>
                </div>

                {org.plan && (
                  <span
                    title={t("admin.accountPlanTitle")}
                    className="shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary border border-primary/20"
                  >
                    {t("admin.accountPlanShort", { plan: org.plan })}
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
